import { useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { useSessionStore } from '@/auth/token-store';
import { isImpersonating } from '@/guidance/api';
import { env } from '@/lib/env';

import type * as RuntimeModule from './runtime';

type Runtime = typeof RuntimeModule;

/**
 * The panel's page tracker (analytics design §6, §12.1). Renders nothing.
 *
 * Mounted in `app/root.tsx` INSIDE `AuthProvider`, which renders its children
 * only once the session has resolved — and after it has consumed the console's
 * `#impersonation=` handoff during its first render. So by the time this runs
 * it knows whether a super-admin is in the chair, and then:
 *
 *  - the build flag `VITE_ANALYTICS` off → nothing, ever;
 *  - impersonating → the tag is never inserted (and `bkUmami` refuses at send
 *    time too, should a session become one later);
 *  - otherwise the tag is loaded once, and every change of PATHNAME reports
 *    the route pattern with its section label. A query-string change (a
 *    filter, a tab) is not a new page.
 *
 * `/login`, `/signup` and `/setup` are under the same root, so they count too.
 */
export function AnalyticsTracker(): null {
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const hydrated = useSessionStore((state) => state.hydrated);
  const impersonatedBy = useSessionStore((state) => state.impersonatedBy);
  const impersonating = Boolean(impersonatedBy) || isImpersonating(queryClient);
  const enabled = env.analytics && hydrated && !impersonating;
  const [runtime, setRuntime] = useState<Runtime | null>(null);

  useEffect(() => {
    if (!enabled || runtime) return;
    let cancelled = false;
    import('./runtime')
      .then((loaded) => {
        if (cancelled) return;
        loaded.loadUmami({
          isImpersonating: () => isImpersonating(queryClient),
          siteUrl: env.siteUrl,
        });
        setRuntime(loaded);
      })
      .catch(() => {
        // A chunk that fails to load costs a pageview, never the panel.
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, runtime, queryClient]);

  // The address last reported: StrictMode's second effect run in development,
  // or a re-render, is not a second visit.
  const reported = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !runtime || reported.current === pathname) return;
    reported.current = pathname;
    const pattern = runtime.routePattern({ pathname });
    runtime.trackPageview(pattern, runtime.sectionLabel(pattern));
  }, [enabled, runtime, pathname]);

  return null;
}
